/* ═══════════════════════════════════════════════════════════════════════════
   Les statistiques du joueur, et leur composition.

   Principe : les modificateurs sont DÉCLARATIFS. Un mod ne contient pas de
   code, il décrit un effet — `{ stat, op, value }` — et le moteur l'applique.
   Deux conséquences qui décident de la suite du projet :

     · ajouter un mod n'est jamais une modification de moteur
     · on peut SIMULER une run entière sans rien afficher, donc éprouver
       l'équilibrage sur des centaines de parties (voir tests/balance)

   Ordre d'application, et il n'est pas anodin : tous les additifs d'abord, tous
   les multiplicatifs ensuite. L'inverse rendrait le résultat dépendant de
   l'ordre de ramassage des cartes — deux joueurs avec le même build n'auraient
   pas les mêmes statistiques, ce qui est indéfendable dans un jeu à score.
   ═══════════════════════════════════════════════════════════════════════════ */

export const Stat = {
  /** Dégâts par projectile. */
  Damage: 'damage',
  /** Volées par seconde. */
  FireRate: 'fireRate',
  /** Projectiles par volée. */
  BulletCount: 'bulletCount',
  BulletSpeed: 'bulletSpeed',
  /** Rayon de la hitbox du tir joueur. */
  BulletRadius: 'bulletRadius',
  MoveSpeed: 'moveSpeed',
  MaxLives: 'maxLives',
  MaxBombs: 'maxBombs',
  /** Rayon de graze — plus il est grand, plus frôler rapporte. */
  GrazeRadius: 'grazeRadius',
  /** Multiplicateur de cellules ramassées. */
  CellGain: 'cellGain',
  /** Facteur appliqué à la recharge des armes secondaires (1 = inchangé,
   *  < 1 = plus rapide).
   *
   *  ⚠️ C'est un multiplicateur où PLUS BAS = MIEUX, contrairement à toutes les
   *  autres statistiques. Une carte de recharge s'écrit donc en `mul` NÉGATIF
   *  (« −20 % de recharge » = `value: -0,2` → 1 × 0,8), et sa borne est un
   *  PLANCHER (`STAT_FLOORS`), pas un plafond. Lu par `secondarySystem`, jamais
   *  par `dps()` : la recharge d'une secondaire n'entre dans aucun DPS primaire. */
  SecondaryCooldownMul: 'secondaryCooldownMul',
  /** Facteur appliqué aux dégâts des armes secondaires (1 = inchangé). Lu par
   *  `secondarySystem`, absent de `dps()` pour la même raison. */
  SecondaryDamageMul: 'secondaryDamageMul',
  /** Capacité du bouclier joueur rechargeable, en charges (1 charge = 1 coup
   *  absorbé). Base ZÉRO : le bouclier est entièrement gagné par les cartes
   *  (`content/mods/bouclier.def.ts`), jamais offert. Un joueur sans amélioration
   *  garde exactement la difficulté de série. Lu par `playerSystem`, absent de
   *  `dps()` — un bouclier n'inflige aucun dégât.
   *
   *  ⚠️ Une carte de capacité s'écrit en `add`, JAMAIS en `mul` : la base vaut
   *  zéro, et `0 × (1 + v)` reste zéro — un multiplicateur ne débloquerait
   *  jamais rien. */
  MaxShield: 'maxShield',
  /** Vitesse de régénération du bouclier, en charges par seconde. Sans effet
   *  tant que `maxShield` vaut zéro. Une carte de recharge l'augmente. */
  ShieldRegen: 'shieldRegen',
  /** TYPE de balle du tir PRIMAIRE — une valeur de `PbMode` (0 = balistique, la
   *  balle historique). Catégoriel, pas quantitatif : une arme le POSE en `add`
   *  depuis zéro, aucune carte ne le multiplie. C'est ce qui permet à une arme de
   *  changer la NATURE du projectile (guidé, etc.) sans nouvelle plomberie —
   *  `playerSystem` le lit et marque la balle émise. Le comportement lui-même vit
   *  dans `pb.mode`/`steerSecondaries`, déjà partagés avec les armes secondaires. */
  BulletMode: 'bulletMode',
  /** Ouverture ANGULAIRE totale de l'ÉVENTAIL du tir primaire, en DEGRÉS. Base
   *  ZÉRO : le tir de série et toutes les armes historiques partent tout droit.
   *
   *  ⚠️ Catégoriel comme `bulletMode`, pas quantitatif : seule l'arme ÉVENTAIL la
   *  POSE (en `add`), aucune carte ne la multiplie. `playerSystem` la lit et donne
   *  un `vx` aux balles du FUSELAGE — un demi-écart de part et d'autre de l'axe.
   *  Neutre (0) par défaut, et c'est ce qui garde le golden intact : à zéro, le
   *  chemin d'émission reste l'ancien (`vx = 0`, `vy = bulletSpeed`), sans passer
   *  par la trigonométrie tabulée — donc au bit près la balle d'avant. */
  BulletSpread: 'bulletSpread',
} as const;

export type StatId = (typeof Stat)[keyof typeof Stat];

export type ModifierOp = 'add' | 'mul';

export interface Modifier {
  readonly stat: StatId;
  readonly op: ModifierOp;
  readonly value: number;
}

export type StatBlock = Record<StatId, number>;

/** Statistiques de départ — celles du VULCAN (PLAN.md §6.3).
 *  10 volées/s × 2 projectiles × 10 dégâts = 200 DPS. C'est l'unité de mesure
 *  de tout l'équilibrage du jeu. */
export const BASE_STATS: StatBlock = {
  damage: 10,
  fireRate: 10,
  bulletCount: 2,
  bulletSpeed: 1800,
  bulletRadius: 14,
  moveSpeed: 480,
  maxLives: 3,
  maxBombs: 2,
  grazeRadius: 45,
  cellGain: 1,
  // 1 = la recharge et les dégâts de secondaire des définitions, inchangés.
  // Neutres par défaut : une run sans carte ni vaisseau spécial tire sa
  // secondaire exactement comme son `SecondaryDef` l'annonce.
  secondaryCooldownMul: 1,
  secondaryDamageMul: 1,
  // Aucun bouclier de série. Une charge de régénération est déjà « armée » : dès
  // qu'une carte débloque la capacité, la jauge remonte sans qu'il faille aussi
  // acheter la recharge. 0,6/s → une charge revient en ~1,7 s de régénération
  // (après le délai d'attente géré par `collisionSystem`).
  maxShield: 0,
  shieldRegen: 0.6,
  // 0 = PbMode.Ballistic : le tir de série est une balle droite, comme depuis le
  // premier jour. Seule une arme au projectile particulier (le Traqueur) le pose.
  bulletMode: 0,
  // 0° d'éventail : le tir part tout droit. Seule l'arme ÉVENTAIL l'ouvre ; une
  // run ordinaire ne le touche jamais, donc le comportement de série est intact.
  bulletSpread: 0,
};

/** Plafonds durs. Ils existent parce qu'un build dégénéré n'est pas un build :
 *  au-delà d'une certaine cadence ou d'un certain nombre de projectiles, le jeu
 *  cesse d'être lisible et le plaisir s'effondre — y compris pour le joueur qui
 *  vient de construire ce build. */
/* ⚠️ Ces plafonds doivent rester HORS DE PORTÉE d'une run normale.
   Le banc d'équilibrage a révélé pourquoi : quand un plafond mord, un mod à
   double effet — « +30 % de cadence, −8 % de dégâts » — ne conserve que son
   malus et devient STRICTEMENT NUISIBLE. Le joueur qui le prend est puni pour
   avoir choisi, alors que le plan interdit formellement qu'un mod soit un piège
   mort (§6.7).
   Ils restent utiles comme filet contre un build dégénéré ou un mod mal réglé,
   mais ce sont des garde-fous de dernier recours, pas des leviers d'équilibrage. */
export const STAT_CAPS: Partial<Record<StatId, number>> = {
  fireRate: 45,
  bulletCount: 10,
  moveSpeed: 900,
  maxLives: 6,
  maxBombs: 4,
  grazeRadius: 140,
  // Les dégâts d'une secondaire ne peuvent pas plus que doubler-et-demi. Même
  // au nominal `validateSecondaries` maintient une secondaire sous la primaire ;
  // sans ce plafond, un build qui empile les cartes de dégâts secondaires
  // referait par la fenêtre l'arme principale que la borne interdit à une
  // définition. 2,5 laisse le levier généreux tout en restant sous la primaire.
  secondaryDamageMul: 2.5,
  // Garde-fou de dernier recours contre un empilement dégénéré de bouclier : une
  // barrière qui encaisserait dix coups d'affilée rendrait le joueur
  // trivialement invincible, ce que le plan interdit. Hors de portée d'une run
  // normale — les cartes de `bouclier.def.ts` plafonnent bien en dessous.
  maxShield: 4,
};

/** Planchers durs — pour les statistiques où PLUS BAS = MIEUX.
 *
 *  `STAT_CAPS` borne par le haut ; un multiplicateur que les cartes tirent vers
 *  zéro a besoin de l'inverse, sinon le floor à 0 de `computeStats` le
 *  laisserait descendre jusqu'à une recharge quasi nulle. */
export const STAT_FLOORS: Partial<Record<StatId, number>> = {
  // Recharge de secondaire divisée par 2,5 au maximum. En deçà de 0,4 le clic
  // droit devient une primaire bis — exactement ce que la borne [2, 8] s de
  // `validateSecondaries` refuse dans l'esprit. Le FANAL (×0,5) plus une carte
  // de recharge atteint tout juste ce plancher ; il ne le franchit jamais.
  secondaryCooldownMul: 0.4,
};

export function emptyStats(): StatBlock {
  return { ...BASE_STATS };
}

/**
 * Compose les statistiques finales.
 *
 * ⚠️ Additifs d'abord, multiplicatifs ensuite, PUIS plafonnement. Cet ordre
 * rend le résultat indépendant de l'ordre dans lequel les cartes ont été
 * ramassées — condition nécessaire pour qu'un score soit comparable.
 */
export function computeStats(mods: readonly Modifier[], base: StatBlock = BASE_STATS): StatBlock {
  const out = { ...base } as StatBlock;

  for (const m of mods) {
    if (m.op === 'add') out[m.stat] = out[m.stat] + m.value;
  }
  for (const m of mods) {
    if (m.op === 'mul') out[m.stat] = out[m.stat] * (1 + m.value);
  }

  for (const k of Object.keys(out) as StatId[]) {
    const cap = STAT_CAPS[k];
    if (cap !== undefined && out[k] > cap) out[k] = cap;
    // Plancher AVANT le floor à 0 : une statistique bornée par le bas (les
    // multiplicateurs de secondaire) ne doit jamais tomber sous son plancher,
    // et le plancher est toujours positif, donc le floor à 0 ci-dessous ne
    // s'applique qu'aux statistiques ordinaires.
    const floor = STAT_FLOORS[k];
    if (floor !== undefined && out[k] < floor) out[k] = floor;
    if (out[k] < 0) out[k] = 0;
  }

  // Le nombre de projectiles est un entier : une volée de 3,4 balles n'existe
  // pas, et arrondir au moment du tir produirait une cadence irrégulière.
  out.bulletCount = Math.floor(out.bulletCount);
  return out;
}

/**
 * DPS mono-cible — la grandeur de référence de tout l'équilibrage.
 *
 * Volontairement simple : cadence × projectiles × dégâts. Elle ignore la
 * dispersion, la perforation et le temps de vol, ce qui est assumé — on veut
 * une mesure STABLE pour comparer des builds entre eux, pas une prédiction
 * exacte du temps de destruction.
 */
export function dps(s: StatBlock): number {
  return s.fireRate * s.bulletCount * s.damage;
}

/* La courbe de puissance cible vit dans `src/game/curve.ts` : elle utilise
   `Math.pow`, interdite ici, et c'est justifié — c'est une référence de
   CONCEPTION lue par le banc d'équilibrage, jamais une mécanique de jeu. */

/* ── Expérience ────────────────────────────────────────────────────────── */

/** `XP(n) = 50 + 30n + 2n²` (PLAN.md §6.7). Quadratique douce : les premiers
 *  niveaux tombent vite — la première récompense doit arriver avant que le
 *  joueur se demande si le système existe — et les derniers se méritent. */
export function xpForLevel(n: number): number {
  return 50 + 30 * n + 2 * n * n;
}

export const MAX_LEVEL = 20;

/** XP cumulée nécessaire pour atteindre `level`. */
export function totalXpFor(level: number): number {
  let sum = 0;
  for (let i = 1; i < level; i++) sum += xpForLevel(i);
  return sum;
}
